// useEntprellteSuche.ts — Tippsuche mit Entprellung, Abbruch und Zeitlimit.
//
// Hintergrund (01.10.2026): In der Kontaktanlage blieb die Firmensuche unter
// „Unternehmen" bei „Suche …" stehen, ohne dass die Anfrage je beim Backend
// ankam (DEV-Backend startete gerade neu). Die drei Suchfelder mit
// „neu eintippen" (ContactRefField, ArbeitgeberAuswahl, Verein) hatten je eine
// eigene Kopie derselben Logik: Ein Fehler wurde verschluckt, eine Anfrage ohne
// Antwort ließ den Lade-Hinweis unbegrenzt stehen, und ein Wiederholen ging nur
// über das Ändern des Suchbegriffs. Dieser Hook bündelt die Logik:
//  - die Lade-Anzeige endet IMMER (Treffer, leer, Fehler, Zeitlimit, Ablösung
//    durch eine neue Eingabe, Wechsel in den Auswahl-Modus);
//  - nur die jeweils jüngste Anfrage darf den Zustand setzen;
//  - ein Fehler (Netz, HTTP ≥ 400, Zeitlimit) wird als `fehler` gemeldet und
//    lässt sich per `erneut()` wiederholen.

import * as React from "react";

export const SUCHE_MIN_ZEICHEN = 2;
/** Nach dieser Zeit ohne Antwort gilt die Suche als fehlgeschlagen. */
export const SUCHE_ZEITLIMIT_MS = 15_000;

export type EntprellteSuche<T> = {
  treffer: T[];
  /** Eine Anfrage läuft (oder ist entprellt angesetzt). */
  laedt: boolean;
  /** Die letzte Anfrage ist fehlgeschlagen (Netz, HTTP-Fehler, Zeitlimit). */
  fehler: boolean;
  /** Der Fehler war das Zeitlimit (Server hat nicht rechtzeitig geantwortet). */
  zuLange: boolean;
  /** Dieselbe Suche noch einmal auslösen (nach einem Fehler). */
  erneut: () => void;
};

/**
 * @param term      der eingetippte Suchbegriff (ungetrimmt)
 * @param aktiv     false → nicht suchen (z. B. Kontakt schon gewählt / Eintipp-Modus)
 * @param laden     holt die Treffer; MUSS das Signal an `fetch` weitergeben und
 *                  bei HTTP-Fehlern werfen. Wird über eine Ref gelesen — eine
 *                  neue Funktions-Identität löst keine neue Suche aus.
 * @param schluessel zusätzliche Werte, deren Änderung eine neue Suche auslöst
 *                  (z. B. ein Typ-Filter)
 */
export function useEntprellteSuche<T>(
  term: string,
  aktiv: boolean,
  laden: (term: string, signal: AbortSignal) => Promise<T[]>,
  {
    debounceMs = 300,
    zeitlimitMs = SUCHE_ZEITLIMIT_MS,
    minZeichen = SUCHE_MIN_ZEICHEN,
    schluessel = "",
  }: { debounceMs?: number; zeitlimitMs?: number; minZeichen?: number; schluessel?: string } = {},
): EntprellteSuche<T> {
  const [treffer, setTreffer] = React.useState<T[]>([]);
  const [laedt, setLaedt] = React.useState(false);
  const [fehler, setFehler] = React.useState(false);
  const [zuLange, setZuLange] = React.useState(false);
  const [versuch, setVersuch] = React.useState(0);
  const ladenRef = React.useRef(laden);
  ladenRef.current = laden;
  const begriff = term.trim();

  React.useEffect(() => {
    if (!aktiv || begriff.length < minZeichen) {
      setTreffer([]);
      setLaedt(false);
      setFehler(false);
      setZuLange(false);
      return;
    }
    let abgeloest = false;
    const ctrl = new AbortController();
    let zeitlimit: ReturnType<typeof setTimeout> | undefined;
    let zeitUeberschritten = false;
    // Sofort „Suche …" zeigen — auch während der Entprellung; sonst blitzt
    // zwischen Tippen und Anfrage kurz „Keine Treffer" auf.
    setLaedt(true);
    setFehler(false);
    setZuLange(false);
    const entprellung = setTimeout(() => {
      zeitlimit = setTimeout(() => {
        zeitUeberschritten = true;
        ctrl.abort();
      }, zeitlimitMs);
      mitAbbruch(ladenRef.current(begriff, ctrl.signal), ctrl.signal)
        .then((liste) => {
          if (abgeloest) return;
          setTreffer(Array.isArray(liste) ? liste : []);
        })
        .catch((e) => {
          if (abgeloest) return; // von einer neueren Eingabe abgelöst — kein Fehler
          console.warn("[Suche] fehlgeschlagen", e);
          setTreffer([]);
          setFehler(true);
          setZuLange(zeitUeberschritten);
        })
        .finally(() => {
          clearTimeout(zeitlimit);
          if (!abgeloest) setLaedt(false);
        });
    }, debounceMs);
    return () => {
      abgeloest = true;
      clearTimeout(entprellung);
      clearTimeout(zeitlimit);
      ctrl.abort();
    };
  }, [begriff, aktiv, minZeichen, debounceMs, zeitlimitMs, schluessel, versuch]);

  const erneut = React.useCallback(() => setVersuch((v) => v + 1), []);
  return { treffer, laedt, fehler, zuLange, erneut };
}

/**
 * Lässt `p` spätestens beim Abbruch von `signal` scheitern — auch wenn die
 * Ladefunktion das Signal nicht (oder nicht bis in jeden Schritt) beachtet.
 * Sonst hinge die Lade-Anzeige an einer Antwort, die nie kommt.
 */
export function mitAbbruch<T>(p: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(new DOMException("Abgebrochen", "AbortError"));
  return new Promise<T>((resolve, reject) => {
    const beiAbbruch = () => reject(new DOMException("Abgebrochen", "AbortError"));
    signal.addEventListener("abort", beiAbbruch, { once: true });
    p.then(resolve, reject).finally(() => signal.removeEventListener("abort", beiAbbruch));
  });
}

/** `fetch` + JSON, wirft bei HTTP-Fehlern (statt still „keine Treffer"). */
export async function holeJsonListe(url: string, signal: AbortSignal): Promise<any[]> {
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const d = await r.json();
  return Array.isArray(d) ? d : [];
}
